import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Alert } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Form, Input, FormField } from "semantic-ui-react";
import PageTitle from "../../components/PageTitle/PageTitle";
import Swal from "sweetalert2";
import "./ForgotPassword.css";
import { useAuth } from "../../context/authContext";
import AuthedUser from "../AuthedUser/AuthedUser";
import { ALERT_MESSAGES, PLACEHOLDER, ROUTES, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { AuthPageStyles } from "../../utilities/themeColors";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { forgotPassword } from "../../services/forgotPasswordService";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";

const ForgotPassword = () => {
	const { t } = useTranslation();
	const { authBackdropStyle } = useBrandingAssets();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(t('UI_TEXT.PASSWORD_RESET_INFO'));
    const [emailError, setEmailError] = useState("");
    const navigate = useNavigate()

    useEffect(() => {
        setMessage(t('UI_TEXT.PASSWORD_RESET_INFO'));
    }, [t]);

    const { isAuthenticated } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            setEmailError(t('ALERT_MESSAGES.REQUIRED_EMAIL'));
            return;
        }
        setEmailError("");
        setLoading(true);

        const sendPasswordEmail = async () => {
            try {
                const req_body = {
                    userId: email,
                };
                const response = await forgotPassword(req_body);
                if (response?.status) {
                    Swal.fire({
                        icon: "success",
                        title: t('ALERT_MESSAGES.SENT'),
                        text: t('UI_TEXT.RESET_LINK_PASSWORD'),
                        timer: 2000,          // 2 seconds
                        showConfirmButton: false,
                    }).then(() => {
                        navigate("/login");
                    });
                } else {
                    setEmailError(response?.message || t('ALERT_MESSAGES.ERROR_OCCURED'));
                }
            } catch (err) {
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('ALERT_MESSAGES.FAILED_TO_SEND_PASSWORD_RESET_LINK'),
                });
                setEmailError(t('ALERT_MESSAGES.ERROR_OCCURED'));
            } finally {
                setLoading(false);
            }
        };

        sendPasswordEmail();
    };

    if (isAuthenticated) {
        return <AuthedUser />;
    }

    return (
        <Box className="login-container" sx={authBackdropStyle || AuthPageStyles}>
            <Box className="login-form-container">
                <CompanyLogo alt="Logo" className="login-logo" />
                <div className="form-container">
                    <PageTitle title={t('TITLES.FORGOT_PASSWORD')} />
                    <Typography variant="body" textAlign="center" marginTop="15px">
                        {t('UI_TEXT.ENTER_YOUR_EMAIL_FOR_PASSWORD_RESET')}
                    </Typography>
                    {emailError && (
                        <Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
                            {emailError}
                        </Alert>
                    )}
                    <Form className="login-form" onSubmit={handleSubmit}>
                        <Form.Field required error={!!emailError}>
                            <Input
                                placeholder={t('TABLE_HEADERS.EMAIL')}
                                type="email"
                                className="login-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Form.Field>
                        <div className="login-button">
                            <Button
                                variant="contained"
                                color="primary"
                                className="button-primary"
                                type="submit"
                                sx={{ minWidth: "120px" }}
                                disabled={loading}>
                                {loading ? t('BUTTON.SENDING_RESET_LINK') : t('BUTTON.RESET_PASSWORD')}
                            </Button>
                        </div>
                    </Form>
                    {/* Additional messages are shown above based on server response */}
                    <Typography variant="body2" color="textSecondary" textAlign="center" marginTop="15px">
                        {message}
                    </Typography>
                </div>
                <Link to={ROUTES.LOGIN} className="login-link">
                    {t('UI_TEXT.BACK_TO_LOGIN')}
                </Link>
            </Box>
        </Box>
    );
};

export default ForgotPassword;
